'use client'

import {
  Users,
  Bot,
  Clock,
  DollarSign,
  ArrowRight,
  Layers,
  UserCheck,
  Zap,
  Building2,
  TrendingDown,
} from 'lucide-react'

/**
 * AI-Native vs Traditional Agency Diagrams
 * Visual components for the agency comparison blog post
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

// 1. Agency Model Comparison — Side-by-side team structure
export function AgencyModelComparisonDiagram() {
  const traditional = [
    { role: 'Creative Director', icon: UserCheck, color: '#ef4444' },
    { role: 'Senior Designer', icon: Users, color: '#ef4444' },
    { role: 'Junior Designer', icon: Users, color: '#ef4444' },
    { role: 'Copywriter', icon: Users, color: '#ef4444' },
    { role: 'Developer', icon: Users, color: '#ef4444' },
    { role: 'Project Manager', icon: Users, color: '#f97316' },
    { role: 'Account Manager', icon: Users, color: '#f97316' },
  ]

  const aiNative = [
    { role: 'Strategic Director', icon: UserCheck, color: '#008dcc' },
    { role: 'AI-Augmented Generalist', icon: Zap, color: '#008dcc' },
    { role: 'AI Design Tools', icon: Bot, color: '#22c55e' },
    { role: 'AI Code Generation', icon: Bot, color: '#22c55e' },
    { role: 'AI QA & Testing', icon: Bot, color: '#22c55e' },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          TEAM STRUCTURE COMPARISON
        </h3>
        <p className='text-sm text-muted-foreground'>
          7 specialists with coordination overhead vs. 2 humans + AI
          collaboration
        </p>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        {/* Traditional */}
        <div className='space-y-2'>
          <div className='flex items-center gap-2 mb-3'>
            <Building2 className='w-4 h-4 text-red-500' />
            <span className='text-sm font-bold text-foreground'>
              Traditional Agency
            </span>
            <span className='text-xs text-muted-foreground ml-auto'>
              7 roles
            </span>
          </div>
          {traditional.map((item, idx) => {
            const Icon = item.icon
            return (
              <div
                key={idx}
                className='flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/5 border border-red-500/20'
              >
                <Icon
                  className='w-3.5 h-3.5 flex-shrink-0'
                  style={{ color: item.color }}
                />
                <span className='text-xs text-foreground'>{item.role}</span>
              </div>
            )
          })}
          <div className='mt-2 px-3 py-2 rounded-lg bg-orange-500/10 border border-orange-500/30 text-center'>
            <p className='text-[10px] text-orange-600 dark:text-orange-400 font-medium'>
              Coordination overhead: meetings, handoffs, approvals
            </p>
          </div>
        </div>

        {/* AI-Native */}
        <div className='space-y-2'>
          <div className='flex items-center gap-2 mb-3'>
            <Zap className='w-4 h-4 text-[#008dcc]' />
            <span className='text-sm font-bold text-foreground'>
              AI-Native Agency
            </span>
            <span className='text-xs text-muted-foreground ml-auto'>
              2 humans + AI
            </span>
          </div>
          {aiNative.map((item, idx) => {
            const Icon = item.icon
            return (
              <div
                key={idx}
                className='flex items-center gap-2 px-3 py-2 rounded-lg'
                style={{
                  backgroundColor:
                    item.icon === Bot
                      ? 'rgba(34, 197, 94, 0.05)'
                      : 'rgba(0, 141, 204, 0.05)',
                  borderWidth: 1,
                  borderStyle: 'solid',
                  borderColor:
                    item.icon === Bot
                      ? 'rgba(34, 197, 94, 0.2)'
                      : 'rgba(0, 141, 204, 0.2)',
                }}
              >
                <Icon
                  className='w-3.5 h-3.5 flex-shrink-0'
                  style={{ color: item.color }}
                />
                <span className='text-xs text-foreground'>{item.role}</span>
                {item.icon === Bot && (
                  <span className='text-[9px] text-green-600 dark:text-green-400 ml-auto font-medium'>
                    AI
                  </span>
                )}
              </div>
            )
          })}
          <div className='mt-2 px-3 py-2 rounded-lg bg-[#008dcc]/10 border border-[#008dcc]/30 text-center'>
            <p className='text-[10px] text-[#008dcc] font-medium'>
              Parallel processing — no sequential handoffs
            </p>
          </div>
        </div>
      </div>

      <p className='text-[10px] text-muted-foreground text-center mt-4'>
        Source: Industry analysis based on agency operational models
      </p>
    </div>
  )
}

// 2. Timeline Comparison — Horizontal bar chart
export function TimelineComparisonDiagram() {
  const phases = [
    {
      phase: 'Discovery & Strategy',
      traditional: { weeks: 2.5, label: '2-3 weeks' },
      aiNative: { weeks: 0.7, label: '3-5 days' },
    },
    {
      phase: 'Design & Development',
      traditional: { weeks: 5, label: '4-6 weeks' },
      aiNative: { weeks: 1.5, label: '1-2 weeks' },
    },
    {
      phase: 'Revisions & QA',
      traditional: { weeks: 2.5, label: '2-3 weeks' },
      aiNative: { weeks: 0.7, label: '3-5 days' },
    },
    {
      phase: 'Launch',
      traditional: { weeks: 1.5, label: '1-2 weeks' },
      aiNative: { weeks: 0.3, label: '1-2 days' },
    },
  ]

  const maxWeeks = 6
  const traditionalTotal = '10-15 weeks'
  const aiNativeTotal = '3-4 weeks'

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          DELIVERY TIMELINE: WEBSITE REDESIGN
        </h3>
        <p className='text-sm text-muted-foreground'>
          Same scope — parallel processing vs. sequential handoffs
        </p>
      </div>

      <div className='space-y-5'>
        {phases.map((item, idx) => (
          <div key={idx} className='space-y-1.5'>
            <div className='flex items-center gap-2'>
              <Clock className='w-3.5 h-3.5 text-muted-foreground' />
              <span className='text-xs font-semibold text-foreground'>
                {item.phase}
              </span>
            </div>

            {/* Traditional bar */}
            <div className='flex items-center gap-2'>
              <span className='text-[10px] text-muted-foreground w-16 text-right flex-shrink-0'>
                Traditional
              </span>
              <div className='flex-1 relative h-5 bg-muted rounded-md overflow-hidden'>
                <div
                  className='absolute top-0 left-0 h-full rounded-md flex items-center justify-end pr-2'
                  style={{
                    width: `${Math.max((item.traditional.weeks / maxWeeks) * 100, 8)}%`,
                    backgroundColor: '#ef4444',
                  }}
                >
                  <span className='text-[9px] font-bold text-white whitespace-nowrap'>
                    {item.traditional.label}
                  </span>
                </div>
              </div>
            </div>

            {/* AI-Native bar */}
            <div className='flex items-center gap-2'>
              <span className='text-[10px] text-muted-foreground w-16 text-right flex-shrink-0'>
                AI-Native
              </span>
              <div className='flex-1 relative h-5 bg-muted rounded-md overflow-hidden'>
                <div
                  className='absolute top-0 left-0 h-full rounded-md flex items-center pr-2'
                  style={{
                    width: `${Math.max((item.aiNative.weeks / maxWeeks) * 100, 8)}%`,
                    backgroundColor: '#008dcc',
                  }}
                >
                  <span className='text-[9px] font-bold text-white whitespace-nowrap ml-1'>
                    {item.aiNative.label}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Totals */}
      <div className='grid grid-cols-2 gap-3 mt-6'>
        <div className='p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-center'>
          <p className='text-lg md:text-xl font-bold text-red-500'>
            {traditionalTotal}
          </p>
          <p className='text-[10px] text-muted-foreground'>Traditional total</p>
        </div>
        <div className='p-3 rounded-lg bg-[#008dcc]/10 border border-[#008dcc]/30 text-center'>
          <p className='text-lg md:text-xl font-bold text-[#008dcc]'>
            {aiNativeTotal}
          </p>
          <p className='text-[10px] text-muted-foreground'>AI-Native total</p>
        </div>
      </div>

      <p className='text-[10px] text-muted-foreground text-center mt-4'>
        Timelines represent typical mid-market website redesign projects
        (Source: WebFX, 310 Creative, industry averages)
      </p>
    </div>
  )
}

// 3. Cost Economics Comparison
export function CostComparisonDiagram() {
  const categories = [
    {
      label: 'Website Redesign',
      traditional: { range: '$30K-$80K', midpoint: 55 },
      aiNative: { range: '$8K-$25K', midpoint: 16.5 },
      source: 'WebFX, Tenet, industry avg',
    },
    {
      label: 'Monthly Retainer',
      traditional: { range: '$10K-$30K', midpoint: 20 },
      aiNative: { range: '$5K-$15K', midpoint: 10 },
      source: 'Industry pricing analysis',
    },
    {
      label: 'Brand Identity',
      traditional: { range: '$25K-$100K', midpoint: 62.5 },
      aiNative: { range: '$6K-$20K', midpoint: 13 },
      source: 'Industry pricing analysis',
    },
  ]

  const maxValue = 100

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          COST STRUCTURE: MID-MARKET COMPARISON
        </h3>
        <p className='text-sm text-muted-foreground'>
          Different economics — not just lower pricing
        </p>
      </div>

      <div className='space-y-5'>
        {categories.map((cat, idx) => (
          <div key={idx} className='space-y-1.5'>
            <div className='flex items-center gap-2'>
              <DollarSign className='w-3.5 h-3.5 text-muted-foreground' />
              <span className='text-xs font-semibold text-foreground'>
                {cat.label}
              </span>
            </div>

            {/* Traditional bar */}
            <div className='flex items-center gap-2'>
              <span className='text-[10px] text-muted-foreground w-16 text-right flex-shrink-0'>
                Traditional
              </span>
              <div className='flex-1 relative h-6 bg-muted rounded-md overflow-hidden'>
                <div
                  className='absolute top-0 left-0 h-full rounded-md flex items-center justify-end pr-2'
                  style={{
                    width: `${(cat.traditional.midpoint / maxValue) * 100}%`,
                    backgroundColor: '#ef4444',
                  }}
                >
                  <span className='text-[9px] font-bold text-white whitespace-nowrap'>
                    {cat.traditional.range}
                  </span>
                </div>
              </div>
            </div>

            {/* AI-Native bar */}
            <div className='flex items-center gap-2'>
              <span className='text-[10px] text-muted-foreground w-16 text-right flex-shrink-0'>
                AI-Native
              </span>
              <div className='flex-1 relative h-6 bg-muted rounded-md overflow-hidden'>
                <div
                  className='absolute top-0 left-0 h-full rounded-md flex items-center pr-2'
                  style={{
                    width: `${Math.max((cat.aiNative.midpoint / maxValue) * 100, 12)}%`,
                    backgroundColor: '#008dcc',
                  }}
                >
                  <span className='text-[9px] font-bold text-white whitespace-nowrap ml-1'>
                    {cat.aiNative.range}
                  </span>
                </div>
              </div>
            </div>
            <p className='text-[10px] text-muted-foreground text-right'>
              {cat.source}
            </p>
          </div>
        ))}
      </div>

      {/* Key insight */}
      <div className='mt-6 p-3 rounded-lg bg-[#008dcc]/10 border border-[#008dcc]/30'>
        <div className='flex items-start gap-2'>
          <TrendingDown className='w-4 h-4 text-[#008dcc] flex-shrink-0 mt-0.5' />
          <div>
            <p className='text-xs font-medium text-foreground'>
              60-75% cost reduction
            </p>
            <p className='text-[10px] text-muted-foreground'>
              Driven by fewer specialists, no coordination overhead, and
              AI-powered execution — not lower quality
            </p>
          </div>
        </div>
      </div>

      <p className='text-[10px] text-muted-foreground text-center mt-4'>
        Sources: WebFX, 310 Creative, SpaceO Technologies, industry pricing
        surveys 2025-2026
      </p>
    </div>
  )
}

// 4. Agency Transformation Trend
export function AgencyTransformationDiagram() {
  const stats = [
    {
      value: '88%',
      label: 'of orgs use AI in at least one function',
      source: 'McKinsey, 2025',
      color: '#008dcc',
    },
    {
      value: '15%',
      label: 'agency role reduction predicted for 2026',
      source: 'Forrester, 2026',
      color: '#F90B8A',
    },
    {
      value: '4.8x',
      label: 'faster productivity growth in AI-adopting industries',
      source: 'McKinsey, 2025',
      color: '#22c55e',
    },
    {
      value: '62%',
      label: 'of organizations use or experiment with AI agents',
      source: 'McKinsey, 2025',
      color: '#8b5cf6',
    },
  ]

  return (
    <div
      data-blog-layout='narrow'
      className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'
    >
      <div className='text-center mb-6'>
        <h3 className='text-xl md:text-2xl font-bold text-foreground mb-2'>
          THE INDUSTRY SHIFT IS ACCELERATING
        </h3>
        <p className='text-sm text-muted-foreground'>
          AI adoption is restructuring agency economics
        </p>
      </div>

      <div className='grid grid-cols-2 gap-3'>
        {stats.map((stat, idx) => (
          <div
            key={idx}
            className='p-4 rounded-xl border text-center'
            style={{
              backgroundColor: `${stat.color}08`,
              borderColor: `${stat.color}30`,
            }}
          >
            <p
              className='text-2xl md:text-3xl font-bold'
              style={{ color: stat.color }}
            >
              {stat.value}
            </p>
            <p className='text-[10px] text-muted-foreground mt-1 leading-tight'>
              {stat.label}
            </p>
            <p className='text-[9px] text-muted-foreground mt-1.5'>
              {stat.source}
            </p>
          </div>
        ))}
      </div>

      <div className='mt-5 flex items-center gap-2 justify-center'>
        <Layers className='w-4 h-4 text-muted-foreground' />
        <ArrowRight className='w-3 h-3 text-muted-foreground' />
        <span className='text-xs text-muted-foreground'>
          Traditional agencies are under pressure to transform or lose market
          share
        </span>
      </div>
    </div>
  )
}
